import dayjs from "dayjs";
import _ from "lodash";
import numeral from "numeral";

import { NOT_AVAILABLE } from "app/filters/const";
import {
    booleanSearchToString,
    booleanSearchToStringIncludeTerms,
} from "app/pages/website-analysis/traffic-sources/search/booleanSearchUtility";
import { AssetsService } from "app/services/AssetsService";
import type { ICountryObject } from "app/services/CountryService";
import CountryService from "app/services/CountryService";
import { KEYWORD_GROUP_PREFIX } from "app/services/keyword-groups";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import type { NavigatorType } from "single-spa/react-app/ProNavigator";

import { i18nFilter } from "./i18nFilter";
import { numberFilter as number, pureNumberFilter } from "./numberFilter";

const MAX_VALUE_TO_DISPLAY_DECIMAL = 1000;

type NumberThreshold = number | null | undefined;
export type ThresholdOptions = {
    min?: NumberThreshold;
    max?: NumberThreshold;
};

export const isNumberAndNotNan = (val: FixmeType) => !isNaN(val) && _.isNumber(val);

const unknownToNumber = (arg: unknown, fallbackValue = 0): number => {
    return isNumberAndNotNan(arg) ? (arg as number) : fallbackValue;
};

/**
 * Calculates the sum of two unknown values.
 * If one of the values is not a number, it is counted as <fallbackValue> ;
 * Examples:
 * 1. sum(1,4) => 5
 * 2. sum (4, "N/A") => 4
 **/
export const sum = (argA: unknown, argB: unknown, fallbackValue = 0) => {
    const a = unknownToNumber(argA, fallbackValue);
    const b = unknownToNumber(argB, fallbackValue);
    return a + b;
};

/** overrides angular.js $filter.number */
export const numberFilter = number;

export const pureNumberFilterWithZeroCount = (num: number, numberTrailingZeros: number): string => {
    if (num === null || num === undefined || !num) {
        return "-";
    }
    let numberTrailingZerosStr = "";
    for (let i = 0; i < numberTrailingZeros; i++) {
        numberTrailingZerosStr += "0";
    }
    if (num < 1000) {
        return numeral(num).format(`0,0a`).toUpperCase();
    }
    return numeral(num).format(`0,0.${numberTrailingZerosStr}a`).toUpperCase();
};

export const pureNumberFixedZerosFilter = () => {
    return (val: number) => pureNumberFilterWithZeroCount(val, 2);
};

const getCountryService = () => CountryService;

export function i18nCategoryFilter() {
    return (key: FixmeType, isChildReq = false) => {
        if (typeof key === "string" && key.startsWith(KEYWORD_GROUP_PREFIX)) {
            return key.substring(1);
        }

        if (typeof key === "string" && key.indexOf("~") !== -1) {
            key = key.replace("~", "/");
        }

        if (typeof key === "string" && key.indexOf("$") !== -1) {
            key = key.replace("$", "");
        }

        if (typeof key === "string") {
            const translationKey = "common.category." + key.toLowerCase();
            let translatedText = i18nFilter()(translationKey);
            const hasTranslation = translatedText.indexOf("common.category.") === -1;

            if (!hasTranslation) {
                // Try new double-underscore format (e.g., "parent__child")
                const doubleUnderscoreKey = key.replace(/\//g, "__");
                const newFormatTranslation = i18nFilter()("common.category." + doubleUnderscoreKey.toLowerCase());
                const hasNewFormatTranslation = newFormatTranslation.indexOf("common.category.") === -1;

                if (hasNewFormatTranslation) {
                    translatedText = newFormatTranslation;
                } else {
                    // No translation found, convert underscores to spaces
                    translatedText = key.replace(/_/g, " ");
                }
            }

            // Extract child name only for child requests
            if (isChildReq) {
                const slashIndex = translatedText.indexOf("/");
                return slashIndex !== -1 ? translatedText.substring(slashIndex + 1) : translatedText;
            }

            // For full path display, ensure parent > child structure is preserved
            const isChildCategory = key.indexOf("/") !== -1;
            const missingParentInTranslation =
                translatedText.indexOf("/") === -1 && translatedText.indexOf(" > ") === -1;

            if (isChildCategory && missingParentInTranslation) {
                return key.replace(/_/g, " ").replace(/\//g, " > ");
            }

            return translatedText.replace(/\//g, " > ");
        }

        return "N/A";
    };
}

export const categoryTextToIconFilter = () => (text: string) =>
    text.replace(/~.+/g, "").replace(/\s+/g, "-").toLowerCase();

export function swNumberFilter() {
    return (val: number | string | undefined | null, decimal?: number, na?: string) => {
        na = na || "N/A";
        decimal = decimal || 0;
        const hasValue = val != null && val !== "";
        return hasValue ? numberFilter()(val, decimal) : na;
    };
}

export function swRankNumberFilter() {
    return (val: number | undefined | null, decimal: number, na: string) => {
        na = "-";
        return val && val > -1 ? "#" + numberFilter()(val, decimal) : '<span class="u-gray-medium">' + na + "</span>";
    };
}

export function swReactRankNumberFilter() {
    return (val: number | undefined | null, decimal: number) => {
        return val && val > -1 ? "#" + numberFilter()(val, decimal) : "-";
    };
}

export function decimalNumberFilter() {
    return (val: FixmeType, removeTrailingZeros: boolean) => {
        const result = !isNaN(val) || typeof val === "string" ? numberFilter()(parseFloat(val as string), 2) : null;

        if (removeTrailingZeros) {
            // @ts-ignore FIXME: strict migration - Argument of type 'string | null' is not assignable to parameter of type 'string' — branch only runs when result is non-null per the isNaN/typeof checks above
            return parseFloat(result).toString();
        } else {
            return result;
        }
    };
}

export function swPositionFilter() {
    return (val: number | string | undefined | null) => (val ? numberFilter()(val) : "-");
}

export function swRankFilter() {
    return (val: number | undefined | null) => (val && val > -1 ? numberFilter()(val) : "-");
}

export function changeFilter() {
    return (val: FixmeType, precision = 2) => {
        if (val > 50) {
            return " > 5,000%";
        } else {
            if (!isNumberAndNotNan(val) || val === 0) {
                return "-";
            }
            const formattedVal = numberFilter()(simplePercentageFilter()(val, precision), precision);
            return pctSignFilter()(formattedVal);
        }
    };
}

export function minimumValueChangeFilter() {
    return (val: number, precision = 2, threshold = 50) => {
        if (val >= threshold) {
            return " > 5,000%";
        } else {
            const conditionalPrecision = val < 10 ? precision : 0;
            const formattedVal = percentageFilter()(val, conditionalPrecision);
            if (!isNaN(Number(formattedVal))) {
                return pctSignFilter()(numberFilter()(Number(formattedVal), conditionalPrecision));
            }
            return pctSignFilter()(formattedVal);
        }
    };
}

export function yAxisChangeFilter() {
    return (val: FixmeType, precision = 0, threshold = 50) => {
        if (val >= threshold) {
            return " > 5000%";
        } else {
            return percentageSignFilter()(val, precision);
        }
    };
}

export const formatShare = (value: number, digits: number, threshold: number, na?: string) => {
    return value < threshold
        ? ` < ${pureNumberFilter(threshold, undefined, na)}%`
        : percentageSignFilter()(value, digits, na);
};

export function spendFilter(val: number, alternative: (...args: FixmeType[]) => FixmeType, threshold?: number) {
    if (threshold && val < threshold) {
        return `< $${alternative(threshold)}`;
    }
    return `$${alternative(val)}`;
}

export function percentageSignFilter() {
    return (val: FixmeType, decimal?: number, na?: string) => {
        na = na || "N/A";
        return isNumberAndNotNan(val) ? simplePercentageFilter()(val, decimal) + "%" : na;
    };
}

export function percentageSignOnlyFilter() {
    return (val: number | undefined | null, decimal: number) =>
        val && !isNaN(val) ? `${(+val).toFixed(+decimal)}%` : "N/A";
}

export function percentageSignOnlyOrZeroFilter() {
    return (val: number | undefined | null, decimal = 0) =>
        val != null && !isNaN(val) ? `${(+val).toFixed(decimal)}%` : "0%";
}

export function percentageFilterWithoutNA() {
    return (val: number | string, decimal: number) => `${(+val).toFixed(+decimal)}%`;
}

export const percentagesignFilter = percentageSignFilter;

export const percentagePointFilter = (
    currentValue: number,
    previousValue: number,
    decimal = 2,
    na?: string,
): string => {
    const difference = (currentValue - previousValue) * 100;

    if (isNaN(difference)) {
        return na ?? "N/A";
    }

    const result = swNumberFilter()(difference, decimal, na);
    return result.toString().concat("pp");
};

export function tinyFractionApproximationFilter() {
    return (targetTemplate: FixmeType, minNumber: number) => {
        const innerNumberString = /(-?\d*\.?\d*)/.exec(targetTemplate);
        const innerNumber = innerNumberString && innerNumberString[1] && parseFloat(innerNumberString[1]);

        // @ts-ignore FIXME: strict migration - 'innerNumber' possibly null — original logic relied on falsy-null < minNumber being false, preserve behavior
        if (innerNumber < minNumber) {
            // @ts-ignore FIXME: strict migration - 'innerNumberString' possibly null — guarded by truthy innerNumber above which requires innerNumberString[1]
            return targetTemplate.toString().replace(innerNumberString[1], "< " + minNumber);
        } else {
            return targetTemplate;
        }
    };
}

export function smallNumbersPercentageFilter() {
    return (val: string, fraction?: number) => {
        const float = parseFloat(val);
        if (!fraction) {
            if (float > 0) {
                if (float < 0.001) {
                    fraction = 5;
                } else if (float < 0.01) {
                    fraction = 4;
                } else if (float < 0.1) {
                    fraction = 3;
                } else {
                    fraction = 0;
                }
            } else {
                fraction = 0;
            }
        }
        return percentageFilter()(val, fraction) + "%";
    };
}

export function simplePercentageFilter() {
    return (val: string | number | null | undefined, fraction?: number, na?: string) => {
        val = Number(val);

        if (isNaN(val)) {
            if (na) {
                return na;
            }

            return 0;
        }

        val *= 100;
        let zeros = "";

        // @ts-ignore FIXME: strict migration - 'fraction' possibly undefined — JS coerces `0 < undefined` to false so the loop is skipped, preserving original behavior
        for (let k = 0; k < fraction; k++) {
            zeros += "0";
        }

        const round = Number("1" + zeros);
        const valBefore = _.clone(val);
        val = Math.round(val * round) / round;
        // zero check is for a small value like 0.04 which can be rounded to 0
        val = val === 0 && valBefore !== 0 ? valBefore.toFixed(fraction || 2) : val.toFixed(fraction);
        // remove trailing zeros
        return Number(val);
    };
}

export function percentageFilter() {
    return (val: FixmeType, fraction: number, unavailable = "0") => {
        val = Number(val);

        if (isNaN(val)) {
            return unavailable ? unavailable : "0";
        }

        if (!val && unavailable) {
            return unavailable;
        }
        const _val = val * 100;
        if (_val === 100) {
            return _val.toString();
        }

        if (100 > _val && _val > 99.95) {
            return "99.9";
        }

        let zeros = "";

        for (let k = 0; k < fraction - 1; k++) {
            zeros += "0";
        }

        const round = Number("1" + zeros + "0");

        if (0 < _val && _val < 1 / round && val !== 0) {
            let ret = " < 0.";
            ret += zeros;
            ret += "1";
            return ret;
        }

        val = Math.round(_val * round) / round;
        val = val.toFixed(fraction);
        return val;
    };
}

export function absFilter() {
    return (val: number) => Math.abs(val);
}

export function prefixNumberFilter() {
    return (val: string | number, sign: string) => {
        const numVal = typeof val === "string" ? numberFilter()(Number(val.replace(/,/g, ""))) : val;

        // @ts-expect-error FIXME: Operator '<' cannot be applied to types 'string | number' and 'number'
        // eslint-disable-next-line no-loss-of-precision
        if (!val || _.isNaN(numVal) || numVal < 1 || numVal >= 9223372036854775807) {
            if (val === "-1") {
                return "-";
            }
            return val || "-";
        }

        return sign + val;
    };
}

export function suffixFilter() {
    return (val: FixmeType, sign: string) => {
        if (val === "0") {
            return "0";
        }

        if (typeof val === "string" && !_.isNaN(Number(val))) {
            return val + sign;
        }

        return val;
    };
}

export function pctSignFilter() {
    return (val: string | number) => val + "%";
}

export function externalLinkFilter(siteName: string) {
    return `http://${siteName}`;
}

export function countryTextByIdFilter() {
    return (val: FixmeType, na = "") => {
        if (val == -1) {
            return i18nFilter()("grid.upgrade");
        }

        const countryService = getCountryService();
        const country = countryService.countriesById[val];
        return country ? country.text || na : "";
    };
}

export function countryByIdFilter() {
    function filter(val: number): ICountryObject;
    function filter<K extends keyof ICountryObject>(val: number, attr: K): ICountryObject[K];
    function filter<K extends keyof ICountryObject>(val: number, attr?: K) {
        const countriesById = getCountryService().countriesById;
        const country = countriesById[val] || countriesById[0];
        if (attr) {
            return country[attr] as unknown;
        }
        return country;
    }
    return filter;
}

export function prettifyCategoryTextFilter() {
    return (name: string, na = "N/A"): string => {
        return name?.replace(new RegExp("_", "gm"), " ")?.replace(new RegExp("~", "gm"), " > ")?.replace("$", "") ?? na;
    };
}

export function categoryIconFilter() {
    return (val: string) => {
        if (!val) {
            return "";
        }
        return val.split(">")[0].split("/")[0].trim().replace(/\s/g, "_").replace(/&/g, "and").toLowerCase();
    };
}

export function categoryClassIconFilter() {
    return (val: FixmeType) => {
        if (!val || !(typeof val === "string")) {
            return "";
        }

        if (val[0] === "*") {
            return "sprite-category custom-category";
        }

        if (val === "All") {
            return "sprite-category all-categories";
        }

        const category = val.split("~");
        return "sprite-category " + category[0];
    };
}

export function categoryLinkFilter(
    swNavigator: NavigatorType,
    pathName = "digitalsuite_marketresearch_webmarketanalysis_overview",
) {
    return (cat: FixmeType, parentCat: FixmeType, ignoreCustomCat = false) => {
        let _cat = cat ? cat.replace("$", "") : "";

        const _parentCat = parentCat ? parentCat.replace("$", "") : "";

        const _params = swNavigator.getParams();

        const _inputParams: { country?: number; category?: string; duration?: string } = {};

        if (!_.isEmpty(_params.country)) {
            _inputParams.country = _params.country;
        }

        if (_params.category && !ignoreCustomCat) {
            const currentCatIsCustom = _params.category[0] == "*";

            if (currentCatIsCustom) {
                _cat = "*" + _cat;
            }
        }

        _inputParams.category = _parentCat && _parentCat != "All" ? _parentCat + "~" + _cat : _cat;

        if (_params.duration) {
            if (_params.duration === "1m" || _params.duration.indexOf("d") !== -1) {
                _inputParams.duration = "3m";
            } else {
                _inputParams.duration = _params.duration;
            }
        }
        return swNavigator.href(pathName, _inputParams);
    };
}

export function categoryPrettyFilter() {
    return (cat: FixmeType, parentCat: FixmeType) => {
        const _cat = cat ? cat.replace("$", "") : "";

        const _parentCat = parentCat ? parentCat.replace("$", "") : "";

        return _parentCat && _parentCat != "All" ? _parentCat + "~" + _cat : _cat;
    };
}

export function subCategoryFilter() {
    return (val: string | null | undefined) => {
        if (!val) {
            return "-";
        }
        const splitted = val.replace("$", "").split("~");
        return i18nCategoryFilter()(splitted[1] || splitted[0]);
    };
}

export function parentCategoryFilter() {
    return (val: string | null | undefined) => {
        if (!val) {
            return "-";
        }
        const splitted = val.replace("$", "").split("~");
        return splitted[0];
    };
}

export function rankFilter() {
    return (val: number) => {
        // eslint-disable-next-line no-loss-of-precision
        if (val === 0 || val === -1 || val >= 9223372036854775807) {
            return "-";
        }

        return numberFilter()(val);
    };
}

export function bigNumberFilter() {
    return (val: number) => {
        if (val < 250) {
            return val;
        }
        /*
         Round the value to make it look more aesthetic
         The chooses what to round(hundreds/thousands etc) based on the number of digits of the number
         */

        const p = Math.floor(Math.log(val) / Math.log(10));
        const r = p % 2 === 1 ? Math.pow(10, (p + 3) / 2) : 5 * Math.pow(10, (p + 2) / 2);
        return Math.round(val / r) * r;
    };
}

export function bigNumberCommaFilter() {
    return (val: number) => {
        const total = bigNumberFilter()(val);
        return swNumberFilter()(total);
    };
}

export function roundNumberFilter() {
    return (val: number, digits = 0) => {
        if (!val) {
            return "N/A";
        }
        const roundCount = Math.pow(10, digits);
        return numberFilter()(Math.round(val / roundCount) * roundCount);
    };
}

export function minVisits(
    val: FixmeType,
    threshold: number,
    alternative: (...args: FixmeType[]) => FixmeType,
    naValue = "N/A",
) {
    if (val === undefined || val === null) {
        return naValue;
    }
    if (!val) {
        return 0;
    }

    return val < threshold ? `< ${alternative(threshold)}` : alternative(val);
}

function minDetailedVisits(val: FixmeType, threshold = 0, alternative: (...args: FixmeType[]) => FixmeType) {
    if (val === undefined || val === null || !val) {
        return `< ${alternative(threshold)}`;
    }

    return val < threshold ? `< ${alternative(threshold)}` : alternative(val);
}

function minDetailedPageViews(val: FixmeType, threshold = 0, alternative: (...args: FixmeType[]) => FixmeType) {
    if (val === undefined || val === null || !val) {
        return `-`;
    }

    return val < threshold ? `< ${alternative(threshold)}` : alternative(val);
}

export function minVisitsFilter() {
    return (val: FixmeType) => minVisits(val, 0, numberFilter());
}

export function minVisitsAbbrFilter() {
    return (val: FixmeType, threshold: number | string = 0) =>
        minVisits(val, Number(threshold), abbrNumberVisitsFilter());
}

export function minDetailedVisitsAbbrFilter() {
    return (val: FixmeType, threshold: number | string = 0) =>
        minDetailedVisits(val, Number(threshold), abbrNumberVisitsFilter());
}

export function minDetailedPageViewsAbbrFilter() {
    return (val: FixmeType, threshold: number | string = 0) =>
        minDetailedPageViews(val, Number(threshold), abbrNumberVisitsFilter());
}

export function minAbbrNumberFilter() {
    return (val: FixmeType, threshold = 0) => minVisits(val, threshold, abbrNumberFilter());
}

export function timeFilter() {
    return (val: "NA" | number | null | undefined, unavailable?: string | null) => {
        if (!val && unavailable) {
            return unavailable;
        }

        if (val === 0) {
            return "00:00:00";
        }
        if (val === null || val === "NA" || !val) {
            return "N/A";
        }

        const isNegative = val < 0;

        if (isNegative) {
            val *= -1;
        }

        const duration = dayjs.duration(val * 1000);
        const calcHours = duration.days() > 0 ? duration.days() * 24 + duration.hours() : duration.hours();
        const h = calcHours > 9 ? calcHours : "0" + calcHours;
        const m = duration.minutes() > 9 ? duration.minutes() : "0" + duration.minutes();
        const s = duration.seconds() > 9 ? duration.seconds() : "0" + duration.seconds();
        let result = h + ":" + m + ":" + s;

        if (isNegative) {
            result = "-" + result;
        }

        return result;
    };
}

export function capitalizeFirstLetterFilter() {
    return (val?: string): string | undefined => {
        if (val && val.indexOf("www") === 0) {
            return val;
        }

        return val && val.charAt(0).toUpperCase() + val.slice(1);
    };
}

export function minusNAFilter() {
    return (val: FixmeType) => (val ? val : "-");
}

export function domain_outFilter() {
    return (val: FixmeType, append: string, overrideLinkText: string) => {
        let href = val;
        append = append || "";

        if (val && val.indexOf("http://") !== 0 && val.indexOf("https://") !== 0) {
            href = "http://" + val;
        }

        const decodedVal = overrideLinkText || val;

        if (val && val !== -1 && !/^n\/a$/i.test(val)) {
            return [
                '<span class="sw-domain-column-name" title="' + val + '">' + decodedVal + "</span>" + append,
                '<a class="sw-link-out" href="' + href + '" target="_blank"></a>',
            ].join("");
        } else {
            return "<span>-</span>";
        }
    };
}

export function abbrNumberSupportNegativeFilter() {
    return (num: number, removeTrailingZeros?: boolean, digitsOverride?: number) => {
        let changeSign = false;
        if (num < 0) {
            changeSign = true;
        }
        const safeRemoveTrailingZeros = removeTrailingZeros === true;
        return changeSign
            ? `-${abbrNumberFilter()(Math.abs(num), safeRemoveTrailingZeros, digitsOverride)}`
            : abbrNumberFilter()(Math.abs(num), safeRemoveTrailingZeros, digitsOverride);
    };
}

export function abbrNumberFilter() {
    return (
        num: string | number | null | undefined,
        removeTrailingZeros?: boolean,
        digitsOverride?: number,
    ): string => {
        if (num == undefined) {
            return "0";
        } else if (typeof num === "string") {
            num = parseFloat(num);

            if (isNaN(num)) {
                return (num = NOT_AVAILABLE);
            }
        }

        let digits,
            suffix = "";

        if (num >= 1000000000) {
            // Billions
            num = num / 1000000000; // Transform 12345678 to 12.3

            digits = 1;
            suffix = "B";
        } else if (num >= 1000000) {
            // Millions
            num = num / 1000000; // Transform 12345678 to 12.3

            digits = 1;
            suffix = "M";
        } else if (num >= 1000) {
            // Thousands
            num = num / 1000; // Transform 12345 to 12.3

            digits = 1;
            suffix = "K";
        } else {
            digits = 2;
            if (!digitsOverride) {
                num = Math.round(num); // fixes #SIM-16442
            }
        }
        // allows overriding the precision.
        if (digitsOverride) {
            digits = digitsOverride;
        }
        num = Math.round(num * Math.pow(10, digits)) / Math.pow(10, digits); //num = numberFilter()(num,digits);

        if (removeTrailingZeros) {
            // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'string'
            return parseFloat(num).toString() + suffix;
        }

        // @ts-expect-error FIXME: Type 'string' is not assignable to type 'number'
        num += suffix;

        // @ts-expect-error "num" is actually of type string after "num += suffix"
        return num;
    };
}

export function abbrNumberWithThresholdFilter() {
    const abbrNumber = abbrNumberFilter();

    return (
        { min, max }: ThresholdOptions,
        num: number | string | null | undefined,
        removeTrailingZeros?: boolean,
        digitsOverride?: number,
    ): string | number => {
        if (typeof num === "undefined" || num === null) {
            return NOT_AVAILABLE;
        }

        const parsedNumber = typeof num === "number" ? num : parseFloat(num);
        const isValidNumber = !Number.isNaN(parsedNumber);

        if (isValidNumber) {
            if (min && parsedNumber < min) {
                return `< ${abbrNumber(min, removeTrailingZeros, digitsOverride)}`;
            }

            if (max && parsedNumber > max) {
                return `> ${abbrNumber(max, removeTrailingZeros, digitsOverride)}`;
            }
        }

        return abbrNumber(parsedNumber, removeTrailingZeros, digitsOverride);
    };
}

export function abbrFloatingNumberFilter() {
    return (num: number | string, removeTrailingZeros?: boolean, digits = 1) => {
        if (typeof num !== "number") {
            num = parseFloat(num);
        }
        if (num < 1000) {
            return Math.round(num * Math.pow(10, digits)) / Math.pow(10, digits);
        }
        return abbrNumberFilter()(num, removeTrailingZeros, digits);
    };
}

export function abbrNumberWithPrefixFilter() {
    return (num: string | number, prefix: string | number) => {
        if (typeof num === "string") {
            num = parseFloat(num);
        }
        return isNaN(num) ? abbrNumberFilter()(num) : `${prefix}${abbrNumberFilter()(num)}`;
    };
}

export function abbrNumberVisitsWithNullFilter() {
    return (num: string | number | null) => {
        if (num === null) {
            return "N/A";
        }
        return abbrNumberVisitsFilter()(num);
    };
}

export function abbrNumberVisitsWithNullOrUndefinedFilter() {
    return (num?: string | number | null) => {
        if (num === null || num === undefined || num === 0) {
            return "N/A";
        }
        return abbrNumberVisitsFilter()(num);
    };
}

export function abbrNumberWithNASupportVisitsFilter() {
    return (val?: number | null) => {
        if (typeof val !== "number" || val === 0) {
            return "N/A";
        }
        return abbrNumberVisitsFilter()(val);
    };
}

export function abbrNumberVisitsFilter() {
    return (num: string | number, abbrThousands?: boolean) => {
        if (typeof num !== "number") {
            return 0;
        }

        if (typeof num === "string") {
            num = parseFloat(num);

            if (isNaN(num)) {
                return 0;
            }
        }

        num = Math.round(num);

        if (String(num).length < (abbrThousands ? 4 : 7)) {
            return numberFilter()(num);
        }

        let res;

        function getSubstrDigitsToShow(num: number, divider: number, letter: string) {
            const digitsToShow = 4;
            let result = String(num / divider).substring(0, digitsToShow + 1); // correct string to show 4 digits always

            let difference = result.length - (digitsToShow + 1);

            if (difference < 0) {
                difference = difference * -1 - 1;
                result += result.indexOf(".") != -1 ? "0" : ".";

                for (let i = 0; i < difference; i++) {
                    result += "0";
                }
            }

            // preserving existing behavior, otherwise should be deleted
            // @ts-expect-error FIXME: Type 'string' is not assignable to type 'number'
            num = result;

            return result + letter;
        }

        if (num >= 1000000000000) {
            // Trillions
            res = getSubstrDigitsToShow(num, 1000000000000, "T");
        } else if (num >= 1000000000) {
            // Billions
            res = getSubstrDigitsToShow(num, 1000000000, "B");
        } else if (num >= 1000000) {
            // Millions
            res = getSubstrDigitsToShow(num, 1000000, "M");
        } else if (num >= 1000) {
            // Thousands
            res = getSubstrDigitsToShow(num, 1000, "K");
        }

        return res;
    };
}

export function appRankChangeFilter() {
    return (value: FixmeType, prefix: string) => {
        prefix = prefix || "";
        return _.isNull(value) || _.isUndefined(value) ? prefix + "-" : value === 0 ? prefix + "=" : Math.abs(value);
    };
}

export function adsFilterFilter(swNavigator: NavigatorType) {
    return (val: string | number) => {
        const currentState = swNavigator.current();
        // @ts-ignore FIXME: strict migration - Property 'Keywords_filters' does not exist on type 'IRouterState' — dynamically attached state param
        delete currentState.Keywords_filters;
        return swNavigator.href(currentState, {
            ...swNavigator.getParams(),
            selectedTab: "ads",
            Ads_filters: "Keyword;contains;" + val,
            search: val,
        });
    };
}

export function searchAdsFilterFilter(swNavigator: NavigatorType, state = "digitalsuite_website_search_ads") {
    return (val: string | number) => {
        const params = swNavigator.getParams();
        return swNavigator.href(
            state,
            _.merge(params, {
                Ads_filters: "Keyword;contains;" + val,
                search: val,
                Ads_page: 1,
                webSource: params.webSource === "Total" || params.webSource === "Desktop" ? "Desktop" : "Mobile",
            }),
        );
    };
}

export function appTitleFilter(chosenItems: FixmeType) {
    return (val: FixmeType) => chosenItems.getById(val).Title;
}

export function appColorFilter(chosenItems: FixmeType) {
    return (val: FixmeType) => (val ? chosenItems.getById(val).Color : "");
}

export function appIconFilter(chosenItems: FixmeType) {
    return (val: FixmeType) =>
        val ? chosenItems.getById(val).Icon : AssetsService.assetUrl("/Images/autocomplete-default.png");
}

export function cpcFilter() {
    return (val: number | null | undefined, threshold = 0) => {
        if (val == 0) {
            return "-";
        }
        if (!val) {
            return NOT_AVAILABLE;
        }
        if (val < MAX_VALUE_TO_DISPLAY_DECIMAL) {
            return "$" + pureNumberFilter(val, "0,0.00[0]");
        } else {
            return "$" + minVisitsAbbrFilter()(val, threshold);
        }
    };
}

export function CPCGroupFilter() {
    return (val: FixmeType, filter?: FixmeType, filterParams: FixmeType[] = []) => {
        if (val === 0 || !val) {
            return "-";
        }
        const cpcFilter = (value: FixmeType) =>
            "$" + (filter ? filter()(parseFloat(value), ...filterParams) : pureNumberFilter(value, "0,0.00[0]"));
        if (val?.includes("-")) {
            const cpcValues = val.split("-");
            const cpcMin = cpcFilter(cpcValues[0]);
            const cpcMax = cpcFilter(cpcValues[1]);
            if (cpcMin === "-" || cpcMax === "-") {
                return cpcMax === "-" ? cpcMin : cpcMax;
            }
            return `${cpcMin} - ${cpcMax}`;
        }
        return cpcFilter(val);
    };
}

export function avgKeywordPositionFilter() {
    return (value: number) => {
        if (value == 0 || value == 999) {
            return "N/A";
        }

        return numberFilter()(value, 0);
    };
}

export function noDataFilter() {
    return (value: FixmeType, noDataString: string, noDataReplacementSymbol: string) => {
        const notDataRegexp = new RegExp(noDataString, "ig");

        if (notDataRegexp.test(value) && value.toString) {
            return value.toString().replace(notDataRegexp, noDataReplacementSymbol);
        } else {
            return value;
        }
    };
}

export function keywordAnalysisHrefFilter(swNavigator: NavigatorType) {
    return (domain: string, params: FixmeType) => {
        const currentParams = swNavigator.getParams();
        let keywordsParams: any = _.pick(currentParams, ["country", "duration"]);
        const isPaid = _.endsWith(currentParams.Keywords_filters, "1") || (params && params.isPaid);

        keywordsParams = {
            ...keywordsParams,
            keyword: domain,
            selectedPageTab: isPaid ? EKeywordTabs[EKeywordTabs.Paid] : EKeywordTabs[EKeywordTabs.Organic],
        };

        return swNavigator.href("organicsearch_keywordAnalysis_2", keywordsParams);
    };
}

export function trafficSourcesSearchHrefFilter(swNavigator: NavigatorType) {
    return (domain: string, webSource: string, booleanSearchTerms: FixmeType[] = []) => {
        webSource = webSource || "Desktop";
        const currentParams = swNavigator.getParams();
        const tsPageParams = _.pick(currentParams, ["country", "duration"]) as any;
        const isPaid = _.endsWith(swNavigator.current().name, "paid");
        const isOrganic = _.endsWith(swNavigator.current().name, "organic");
        const BooleanSearchTerms = booleanSearchToString(booleanSearchTerms);
        const IncludeTerms = booleanSearchToStringIncludeTerms(booleanSearchTerms);

        Object.assign(tsPageParams, {
            key: domain || currentParams.key,
            isWWW: "*",
            Keywords_page: 1,
            webSource,
            IncludeOrganic: isOrganic,
            IncludePaid: isPaid,
            BooleanSearchTerms,
            IncludeTerms, //Hotfix: SIM-31477
        });

        if (isPaid) {
            tsPageParams.Keywords_filters = "OP;==;1";
        } else {
            tsPageParams.Keywords_filters = "OP;==;0";
        }

        return swNavigator.href("organicsearch_websites_v2_traffic_keywords", tsPageParams);
    };
}

export function deviceTrafficShareFilter() {
    return (data: FixmeType, key: string) => {
        if (!data) {
            return "";
        }

        const total = _.sum(_.values(data));

        return percentageSignFilter()(data[key] / total, 2);
    };
}

export function i18nTemplateFilter() {
    return (templateStr: string, params: FixmeType) => {
        // replace text inside %%
        try {
            return _.template(i18nFilter()(templateStr), {
                interpolate: /%([\s\S]+?)%/g,
            })(params);
        } catch (e) {
            return i18nFilter()(templateStr);
        }
    };
}

export function webSourceTextSlimFilter() {
    return (value: string) => {
        const SOURCES_TEXT: Record<string, string> = {
            Total: "All Traffic",
            Combined: "All Traffic",
            MobileWeb: "Mobile Web",
            "Mobile Web": "Mobile Web",
            Desktop: "Desktop",
        };
        return SOURCES_TEXT[value] || "";
    };
}

export function industryKeyToTitleFilter() {
    const customPrefix = "*";
    const keyPrefix = "$";
    const spaceDelimeter = "_";
    const arrowDelim = "~";

    function removePrefix(value: string, keyPrefix: string) {
        if (value && value.length > 0 && value.indexOf(keyPrefix) == 0) {
            return value.substring(1, value.length);
        }

        return value;
    }

    return (value: string) => {
        if (value && value.length > 0) {
            if (value === "$All" || value === "All") {
                return "All categories";
            }
            return removePrefix(
                removePrefix(value, keyPrefix).replace(new RegExp(spaceDelimeter, "g"), " ").replace(arrowDelim, " > "),
                customPrefix,
            );
        }

        return value;
    };
}

export function industryKeyToIconFilter() {
    return (value: string) => {
        if (value === "$All" || value === "All") {
            return "sw-icon-categories";
        } else if (value.substring(0, 1) == "*" || value.substring(0, 2) == "$*") {
            return "sw-icon-custom-categories";
        } else {
            return "sprite-category " + value.split("~")[0].replace("$", "");
        }
    };
}

export function sourceStrength() {
    return (strengthNum: number) => {
        const strengths: Record<number, string> = {
            3: i18nFilter()("apps.storeanalysis.instore.keywords.table.sourcestrength.high"),
            2: i18nFilter()("apps.storeanalysis.instore.keywords.table.sourcestrength.medium"),
            1: i18nFilter()("apps.storeanalysis.instore.keywords.table.sourcestrength.low"),
        };
        return strengths[strengthNum] || "-";
    };
}

export function adsTargetURL() {
    return function (args: FixmeType) {
        const { FullPage, Page, DestUrl } = args;
        let targetURL = FullPage || Page || DestUrl;
        if (targetURL.startsWith("/")) {
            return;
        }
        if (!targetURL.startsWith("http")) {
            targetURL = `http://${targetURL}`;
        }
        return targetURL;
    };
}

export function prettifyConversionCategory(item1: string, item2?: string) {
    return item2 ? _.startCase(item1) + " > " + _.startCase(item2) : _.startCase(item1);
}

export function trimZeroPrefixFromNumberedString(str: string, separator: string) {
    return str
        .split(separator)
        .map((numberAsString) => (numberAsString[0] === "0" ? numberAsString.substring(1) : numberAsString))
        .join(separator);
}
